import {
  AMBIGUOUS_UK,
  AMBIGUOUS_US,
  type AmbiguityNote,
  type SpellingPattern,
  spellingPairs,
} from "@/lib/tool-kit/text-analysis/spelling-data";

/**
 * Convert British spelling to American or back, word by word, keeping the reader's
 * capitalisation and reporting every change.
 *
 * Only words in the pair list change. A word that is not in it — a name, a technical
 * term, an -ise word that is -ise everywhere — passes through untouched, which is the
 * property that makes a dictionary safer than a suffix rule.
 */

export type SpellingDirection = "uk-us" | "us-uk";

export interface SpellingOptions {
  direction: SpellingDirection;
  /** American → British only: keep -ize, as Oxford spelling does. */
  oxford: boolean;
  /** Leave capitalised words in mid-sentence alone — they are usually names. */
  keepCapitalized: boolean;
  /** Lowercase source words the writer chose to keep. */
  keepWords: ReadonlySet<string>;
}

interface Located {
  /** Offsets in the input. */
  start: number;
  end: number;
  /** Offsets in the output. */
  outStart: number;
  outEnd: number;
}

export interface SpellingChange extends Located {
  from: string;
  to: string;
  pattern: SpellingPattern;
}

export interface SpellingFlag extends Located {
  word: string;
  note: AmbiguityNote;
}

export interface KeptWord extends SpellingChange {
  reason: "capitalized" | "chosen";
}

export interface SpellingResult {
  output: string;
  changes: SpellingChange[];
  flags: SpellingFlag[];
  kept: KeptWord[];
}

interface Target {
  to: string;
  pattern: SpellingPattern;
}

const WORD_RE = /\p{L}+/gu;

/** Fixed phrases spelled the same in both varieties: "rigor mortis" is not "rigour". */
const FIXED_PHRASES = new Map<string, RegExp>([["rigor", /^\s+mortis\b/i]]);
const LEADING_RE = /[\s"“‘'([]/;
const SENTENCE_END_RE = /[.!?:…]/;

/**
 * Prefixes peeled off a word that is not in the list itself, so "reorganise",
 * "unfavourable" and "multicoloured" convert without listing every compound. The rest
 * must be at least five letters, so "revise" is never read as re + "vise".
 */
const PREFIXES = [
  "counter",
  "under",
  "inter",
  "multi",
  "hyper",
  "super",
  "micro",
  "macro",
  "over",
  "anti",
  "semi",
  "post",
  "non",
  "mis",
  "dis",
  "pre",
  "sub",
  "bio",
  "un",
  "re",
  "de",
  "co",
];
const MIN_STEM = 5;

const maps = new Map<string, Map<string, Target>>();

function targetMap(direction: SpellingDirection, oxford: boolean) {
  const key = `${direction}:${direction === "us-uk" && oxford}`;
  const hit = maps.get(key);
  if (hit) {
    return hit;
  }
  const map = new Map<string, Target>();
  for (const pair of spellingPairs()) {
    if (pair.only && pair.only !== direction) {
      continue;
    }
    if (direction === "uk-us") {
      map.set(pair.uk, { to: pair.us, pattern: pair.pattern });
      if (pair.oxford && pair.oxford !== pair.us) {
        map.set(pair.oxford, { to: pair.us, pattern: pair.pattern });
      }
      continue;
    }
    const to = oxford ? (pair.oxford ?? pair.uk) : pair.uk;
    if (to !== pair.us) {
      map.set(pair.us, { to, pattern: pair.pattern });
    }
  }
  maps.set(key, map);
  return map;
}

/** The target for a lowercase word, directly or after peeling a prefix. */
export function lookupSpelling(
  word: string,
  direction: SpellingDirection,
  oxford = false
): Target | null {
  const map = targetMap(direction, oxford);
  const direct = map.get(word);
  if (direct) {
    return direct;
  }
  for (const prefix of PREFIXES) {
    if (word.startsWith(prefix) && word.length - prefix.length >= MIN_STEM) {
      const rest = map.get(word.slice(prefix.length));
      if (rest) {
        return { to: prefix + rest.to, pattern: rest.pattern };
      }
    }
  }
  return null;
}

const AMBIGUOUS: Record<
  SpellingDirection,
  ReadonlyMap<string, AmbiguityNote>
> = {
  "uk-us": new Map(Object.entries(AMBIGUOUS_UK)),
  "us-uk": new Map(Object.entries(AMBIGUOUS_US)),
};

/** "COLOUR" → "COLOR", "Colour" → "Color", "colour" → "color". */
export function matchCase(source: string, target: string): string {
  if (source.length > 1 && source === source.toUpperCase()) {
    return target.toUpperCase();
  }
  const first = source[0];
  if (first !== first.toLowerCase()) {
    return target[0].toUpperCase() + target.slice(1);
  }
  return target;
}

/** A capitalised word that does not start a sentence, line or quotation — likely a name. */
function isNameLike(text: string, start: number, word: string): boolean {
  const first = word[0];
  const capitalised = first !== first.toLowerCase();
  if (!capitalised || (word.length > 1 && word === word.toUpperCase())) {
    return false;
  }
  let i = start - 1;
  while (i >= 0 && LEADING_RE.test(text[i]) && text[i] !== "\n") {
    i--;
  }
  return i >= 0 && text[i] !== "\n" && !SENTENCE_END_RE.test(text[i]);
}

export function convertSpelling(
  text: string,
  options: SpellingOptions
): SpellingResult {
  const ambiguous = AMBIGUOUS[options.direction];
  const result: SpellingResult = {
    output: "",
    changes: [],
    flags: [],
    kept: [],
  };
  const parts: string[] = [];
  let cursor = 0;
  let outLength = 0;
  for (const match of text.matchAll(WORD_RE)) {
    const word = match[0];
    const start = match.index;
    const end = start + word.length;
    const between = text.slice(cursor, start);
    parts.push(between);
    outLength += between.length;
    cursor = end;
    const lower = word.toLowerCase();
    const note = ambiguous.get(lower);
    const fixed = FIXED_PHRASES.get(lower)?.test(text.slice(end)) ?? false;
    const target =
      note || fixed
        ? null
        : lookupSpelling(lower, options.direction, options.oxford);
    const located = { start, end, outStart: outLength };
    if (note) {
      result.flags.push({
        ...located,
        outEnd: outLength + word.length,
        word,
        note,
      });
    }
    let written = word;
    if (target) {
      const to = matchCase(word, target.to);
      const keptBy = keptReason(text, start, word, lower, options);
      if (keptBy) {
        result.kept.push({
          ...located,
          outEnd: outLength + word.length,
          from: word,
          to,
          pattern: target.pattern,
          reason: keptBy,
        });
      } else {
        written = to;
        result.changes.push({
          ...located,
          outEnd: outLength + to.length,
          from: word,
          to,
          pattern: target.pattern,
        });
      }
    }
    parts.push(written);
    outLength += written.length;
  }
  parts.push(text.slice(cursor));
  result.output = parts.join("");
  return result;
}

function keptReason(
  text: string,
  start: number,
  word: string,
  lower: string,
  options: SpellingOptions
): KeptWord["reason"] | null {
  if (options.keepWords.has(lower)) {
    return "chosen";
  }
  if (options.keepCapitalized && isNameLike(text, start, word)) {
    return "capitalized";
  }
  return null;
}

export interface ChangeGroup {
  /** Lowercase source word — the key the "keep" choice is stored under. */
  from: string;
  to: string;
  pattern: SpellingPattern;
  count: number;
  /** The writer chose to keep this word unchanged. */
  kept: boolean;
}

/** Changes (and words kept by choice) grouped by word, in order of first appearance. */
export function groupChanges(result: SpellingResult): ChangeGroup[] {
  const groups = new Map<string, ChangeGroup>();
  const chosen = result.kept.filter((kept) => kept.reason === "chosen");
  const all = [...result.changes, ...chosen].sort((a, b) => a.start - b.start);
  for (const item of all) {
    const from = item.from.toLowerCase();
    const group = groups.get(from);
    if (group) {
      group.count++;
      continue;
    }
    groups.set(from, {
      from,
      to: item.to.toLowerCase(),
      pattern: item.pattern,
      count: 1,
      kept: "reason" in item,
    });
  }
  return [...groups.values()];
}
